import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { t } from 'i18next';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminProductReviewState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}

export const defaultColumnList = [
  {
    name: 'product',
    label_key: 'product',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('product') }),
    asyncKey: 'product',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 1,
  },
  {
    name: 'ffl',
    label_key: 'ffl',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: t('colon_all', { key: t('ffl') }),
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 1,
  },
  {
    name: 'user',
    label_key: 'customer',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 2,
  },
  {
    name: 'rating',
    label_key: 'rating',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    selectKey: 'rating',
    filter_placeholder: t('colon_all', { key: t('rating') }),
    option_value_key: 'value',
    option_label_key: 'name',
    multi: false,
    order: 3,
  },
  {
    name: 'review',
    label_key: 'review',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 4,
  },
  {
    name: 'images',
    label_key: 'media',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 5,
  },
  {
    name: 'status',
    label_key: 'status',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: t('colon_all', { key: t('status') }),
    selectKey: 'status',
    option_value_key: 'id',
    option_label_key: 'name',
    multi: false,
    order: 6,
  },
  {
    name: 'created_at',
    label_key: 'created_at',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 7,
  },
];

const exportColumnList = defaultColumnList
  .filter((list) => list.name !== 'images')
  .map((list) => ({
    ...list,
    allow_list: true,
    show_list_checkbox: true,
  }));

const initialState: AdminProductReviewState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminProductReviewSlice = createSlice({
  name: 'adminProductReview',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      console.log('filter data: ', action.payload);
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setProductReviewTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      state.tableColumnList = action.payload;
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setProductReviewTableColumnList,
} = adminProductReviewSlice.actions;

export default adminProductReviewSlice.reducer;
